// Envelope-aware error panel for the live read pages (RFC-0009 Task 1.3, §6).
//
// Surfaces the G13 `error.message` (with the machine `code`, when present) from
// an `ApiError`, plus a retry control that refetches. A 401 never reaches here —
// the interceptor redirects to /login first (Phase 2).

import { WarningCircle, ArrowClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api-client';

interface ErrorPanelProps {
  /** The thrown error — `ApiError` messages/codes are surfaced verbatim. */
  error: unknown;
  /** Retry handler (typically the query's `refetch`). */
  onRetry?: () => void;
  /** Optional heading override. */
  title?: string;
}

function messageFor(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'An unexpected error occurred. Please try again.';
}

export function ErrorPanel({
  error,
  onRetry,
  title = 'Something went wrong',
}: ErrorPanelProps) {
  const code = error instanceof ApiError ? error.code : undefined;

  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center rounded-xl border border-[rgba(255,92,92,0.2)] bg-error-bg px-8 py-12 text-center"
    >
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-surface text-error">
        <WarningCircle size={22} weight="fill" />
      </span>
      <p className="mb-1.5 text-[15px] font-semibold text-text">{title}</p>
      <p className="mb-1 max-w-md text-sm leading-relaxed text-muted">{messageFor(error)}</p>
      {code && (
        <p className="mb-5 font-mono text-[11px] uppercase tracking-wide text-muted/70">{code}</p>
      )}
      {onRetry && (
        <Button size="md" variant="secondary" onClick={onRetry} className={code ? '' : 'mt-4'}>
          <ArrowClockwise size={15} weight="bold" />
          Try again
        </Button>
      )}
    </div>
  );
}
